import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Filter, Plus, Mail, Shield, Search } from 'lucide-react';

export function Projektborsen() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [showPostForm, setShowPostForm] = useState(false);
  
  // Sample categories
  const categories = [
    { id: 'all', name: 'Alla kategorier' },
    { id: 'tech', name: 'Teknik & Innovation' },
    { id: 'science', name: 'Vetenskap & Forskning' },
    { id: 'education', name: 'Utbildning' },
    { id: 'culture', name: 'Kultur & Konst' },
    { id: 'social', name: 'Sociala projekt' },
    { id: 'environment', name: 'Miljö & Hållbarhet' },
    { id: 'health', name: 'Hälsa & Medicin' },
    { id: 'entrepreneurship', name: 'Entreprenörskap' }
  ];
  
  // Sample project listings
  const projectListings = [
    {
      id: 1,
      title: 'Innovativ vattenreningslösning för landsbygden',
      category: 'environment',
      description: 'Vi utvecklar en kostnadseffektiv och hållbar vattenreningslösning för områden utan tillgång till kommunalt vatten. Tekniken använder lokala material och solenergi.',
      neededAmount: '250 000 - 500 000 kr',
      location: 'Västerbotten',
      postedDate: '2025-03-28'
    },
    {
      id: 2,
      title: 'AI-driven plattform för tidig upptäckt av läs- och skrivsvårigheter',
      category: 'tech',
      description: 'Vår plattform använder maskininlärning för att identifiera tidiga tecken på dyslexi och andra läs- och skrivsvårigheter hos barn i förskoleåldern, vilket möjliggör tidigare intervention.',
      neededAmount: '300 000 - 750 000 kr',
      location: 'Stockholm',
      postedDate: '2025-03-30'
    },
    {
      id: 3,
      title: 'Musikterapi för äldre med demens',
      category: 'health',
      description: 'Ett projekt som undersöker och tillämpar musikterapi för att förbättra livskvaliteten för äldre med demens. Vi samarbetar med tre äldreboenden i Malmöområdet.',
      neededAmount: '100 000 - 200 000 kr',
      location: 'Skåne',
      postedDate: '2025-03-25'
    },
    {
      id: 4,
      title: 'Historisk dokumentation av traditionellt hantverk',
      category: 'culture',
      description: 'Vi dokumenterar och bevarar kunskapen om traditionella hantverksmetoder som riskerar att försvinna. Projektet inkluderar videodokumentation och en digital kunskapsbank.',
      neededAmount: '150 000 - 300 000 kr',
      location: 'Dalarna',
      postedDate: '2025-03-29'
    },
    {
      id: 5,
      title: 'Mentorprogram för unga entreprenörer från utsatta områden',
      category: 'entrepreneurship',
      description: 'Ett mentorprogram som kopplar samman erfarna företagare med unga entreprenörer från socioekonomiskt utsatta områden för att främja företagande och ekonomisk egenmakt.',
      neededAmount: '200 000 - 400 000 kr',
      location: 'Göteborg',
      postedDate: '2025-03-27'
    },
    {
      id: 6,
      title: 'Forskningsstudie om biologisk mångfald i stadsmiljö',
      category: 'science',
      description: 'En tvåårig studie som undersöker hur urbana grönområden kan optimeras för att stödja biologisk mångfald samtidigt som de tillgodoser invånarnas rekreationsbehov.',
      neededAmount: '500 000 - 800 000 kr',
      location: 'Uppsala',
      postedDate: '2025-03-26'
    }
  ];
  
  // Filter projects based on active category
  const filteredProjects = activeCategory === 'all' 
    ? projectListings 
    : projectListings.filter(project => project.category === activeCategory);
  
  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#2563eb] mb-3">Projektbörsen</h1>
        <p className="text-gray-600 max-w-3xl">
          Här möts projekt som söker finansiering och stiftelser med medel att fördela. 
          Lägg upp ditt projekt eller bläddra bland befintliga för att hitta en matchning.
        </p>
      </div>
      
      {/* Main content area with sidebar */}
      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar with categories and actions */}
        <div className="w-full md:w-64 flex-shrink-0">
          <div className="bg-white rounded-lg shadow-md p-4 mb-4">
            <h2 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <Filter className="w-4 h-4 text-[#A3C1DA]" />
              Kategorier
            </h2>
            <ul className="space-y-1">
              {categories.map(category => (
                <li key={category.id}>
                  <button
                    onClick={() => setActiveCategory(category.id)}
                    className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                      activeCategory === category.id 
                        ? 'bg-[#A3C1DA] text-white' 
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    {category.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="bg-[#F2F7FF] rounded-lg shadow-md p-4">
            <h2 className="font-semibold text-lg mb-3">Lägg upp ditt projekt</h2>
            <p className="text-sm text-gray-600 mb-4">
              Har du ett projekt som behöver finansiering? Lägg upp det här så kan intresserade stiftelser kontakta dig.
            </p>
            <button
              onClick={() => setShowPostForm(true)}
              className="w-full bg-[#F2C1D1] text-white px-4 py-2 rounded flex items-center justify-center gap-2 hover:bg-[#D1A3B3] transition-colors"
            >
              <Plus className="w-4 h-4" />
              Lägg upp projekt
            </button>
          </div>
          
          <div className="mt-4 bg-[#F2F7FF] rounded-lg shadow-md p-4">
            <h2 className="font-semibold text-lg mb-3">För stiftelser</h2>
            <p className="text-sm text-gray-600 mb-4">
              Har din stiftelse medel kvar att fördela? Bläddra bland projekten och ta kontakt med de som passar era kriterier.
            </p>
            <div className="flex items-center justify-center gap-2 text-[#2563eb] text-sm">
              <Shield className="w-4 h-4" />
              <span>Verifierad kontaktprocess</span>
            </div>
          </div>
        </div>
        
        {/* Main content with project listings */}
        <div className="flex-grow">
          {/* Search and filter bar */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-6 flex flex-col sm:flex-row gap-4 items-center">
            <div className="relative flex-grow">
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Sök bland projekt..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A3C1DA]"
              />
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500 whitespace-nowrap">
              <span>Visar:</span>
              <span className="font-medium text-[#2563eb]">
                {filteredProjects.length} projekt
              </span>
            </div>
          </div>
          
          {/* Project listings */}
          <div className="space-y-6">
            {filteredProjects.length > 0 ? (
              filteredProjects.map(project => (
                <div key={project.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="p-5">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-xl font-semibold text-[#2563eb]">{project.title}</h3>
                      <span className="bg-[#F2F7FF] text-[#2563eb] text-xs px-2 py-1 rounded-full">
                        {categories.find(c => c.id === project.category)?.name || project.category}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 mb-4">{project.description}</p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-500">Sökt belopp</p>
                        <p className="font-medium">{project.neededAmount}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Plats</p>
                        <p className="font-medium">{project.location}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Publicerad</p>
                        <p className="font-medium">{project.postedDate}</p>
                      </div>
                    </div>
                    
                    <button className="bg-[#A3C1DA] text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-[#7A98B0] transition-colors">
                      <Mail className="w-4 h-4" />
                      Kontakta projektet
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white rounded-lg shadow-md p-8 text-center">
                <p className="text-gray-500 mb-4">Inga projekt hittades i denna kategori.</p>
                <button
                  onClick={() => setActiveCategory('all')}
                  className="text-[#2563eb] hover:underline"
                >
                  Visa alla projekt
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Post project form modal */}
      {showPostForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-[#2563eb]">Lägg upp ditt projekt</h2>
                <button 
                  onClick={() => setShowPostForm(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              
              <form className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Projekttitel</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A3C1DA]"
                    placeholder="En beskrivande titel för ditt projekt"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
                  <select className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A3C1DA]">
                    <option value="">Välj kategori</option>
                    {categories.filter(c => c.id !== 'all').map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Projektbeskrivning</label>
                  <textarea
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A3C1DA] h-32"
                    placeholder="Beskriv ditt projekt, dess mål och varför det behöver finansiering"
                  ></textarea>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sökt belopp</label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A3C1DA]"
                      placeholder="t.ex. 100 000 - 250 000 kr"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Plats</label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A3C1DA]"
                      placeholder="t.ex. Stockholm, Hela Sverige"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Din e-postadress</label>
                  <input
                    type="email"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A3C1DA]"
                    placeholder="Din e-post (visas inte offentligt)"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Din e-postadress visas inte offentligt. Intresserade stiftelser kan kontakta dig via ett säkert formulär.
                  </p>
                </div>
                
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    id="terms"
                    className="mt-1"
                  />
                  <label htmlFor="terms" className="text-sm text-gray-600">
                    Jag godkänner att min projektinformation publiceras på Projektbörsen och att jag kan bli kontaktad av stiftelser via e-post.
                  </label>
                </div>
                
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowPostForm(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Avbryt
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#A3C1DA] text-white rounded-md hover:bg-[#7A98B0] transition-colors"
                  >
                    Publicera projekt
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {/* How it works section */}
      <div className="mt-12 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-[#2563eb] mb-4">Hur Projektbörsen fungerar</h2>
        
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-[#F2F7FF] p-4 rounded-lg">
            <div className="w-8 h-8 bg-[#A3C1DA] text-white rounded-full flex items-center justify-center font-bold mb-3">1</div>
            <h3 className="font-semibold mb-2">Lägg upp ditt projekt</h3>
            <p className="text-sm text-gray-600">
              Beskriv ditt projekt, vilket belopp du söker och vilken kategori det tillhör. Din e-postadress hålls privat.
            </p>
          </div>
          
          <div className="bg-[#F2F7FF] p-4 rounded-lg">
            <div className="w-8 h-8 bg-[#A3C1DA] text-white rounded-full flex items-center justify-center font-bold mb-3">2</div>
            <h3 className="font-semibold mb-2">Stiftelser hittar ditt projekt</h3>
            <p className="text-sm text-gray-600">
              Stiftelser med medel att fördela söker bland projekten efter sådana som passar deras ändamål och kriterier.
            </p>
          </div>
          
          <div className="bg-[#F2F7FF] p-4 rounded-lg">
            <div className="w-8 h-8 bg-[#A3C1DA] text-white rounded-full flex items-center justify-center font-bold mb-3">3</div>
            <h3 className="font-semibold mb-2">Säker kontakt</h3>
            <p className="text-sm text-gray-600">
              Intresserade stiftelser kontaktar dig via vårt säkra system. Ni kan sedan fortsätta dialogen direkt via e-post.
            </p>
          </div>
        </div>
        
        <div className="mt-6 p-4 border border-[#A3C1DA] rounded-lg bg-[#F2F7FF]">
          <p className="text-center text-sm">
            <strong>OBS:</strong> Projektbörsen är en kostnadsfri tjänst som förmedlar kontakt mellan projekt och stiftelser. 
            Vi garanterar inte finansiering och är inte involverade i beslut om tilldelning av medel.
          </p>
        </div>
      </div>
      
      <div className="mt-8 pt-4 border-t border-gray-200">
        <Link to="/" className="text-[#2563eb] hover:underline flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" />
          Tillbaka till startsidan
        </Link>
      </div>
    </div>
  );
}